import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

import MatrixPopover from './matrix-popover';
import ColorPopover from './color-popover';
import IconItem from '../../exercise/checker/icon-item';
import { useMouseDownOutside } from '../../../lib/hooks';

const Container = styled.div`
    display: flex;
    flex-wrap: wrap;

    & > button {
        margin-top: 8px;
        margin-right: 8px;
    }

    ${(props) => props.theme.mediaQueries.mobile} {
        margin-left: -6px;
        margin-right: -52px;
    }

    ${(props) => props.theme.mediaQueries.tablet} {
        margin: 0;
        margin-left: -8px;
    }

    ${(props) => props.theme.mediaQueries.desktop} {
        margin: 0;
        margin-left: -8px;
    }
`;

const ButtonContainer = styled.div`
    ${({ theme }) => css`
        position: relative;
        padding-top: 8px;
        padding-left: 4px;

        ${theme.mediaQueries.tablet} {
            padding-top: 16px;
            padding-left: 8px;
        }
    `}
`;

const Button = styled.button`
    ${({ theme }) => css`
        display: flex;
        align-items: center;

        background-color: ${theme.colors['surface-white']};
        border-radius: 4px;
        border: none;
        box-shadow: ${theme.shadows['shadow-01dp']};
        height: 36px;
        outline: 0;
        padding: 0;
        width: 36px;
        cursor: pointer;

        &:hover {
            background-color: ${theme.colors['surface-grey-050']};
            outline: 0;
        }

        &:active {
            background-color: ${theme.colors['surface-grey-200']};
            box-shadow: ${theme.shadows['shadow-02dp']};
            outline: 0;
        }
    `}
`;

const Icon = styled(IconItem)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-myzanichelli-blue-main']};
        height: 100%;
        width: 100%;

        &:hover {
            fill: ${theme.colors['text-myzanichelli-blue-dark']};
        }
    `}
`;

const PopoverContainer = styled.div`
    ${({ theme }) => css`
        position: absolute;
        top: 43px;
        z-index: 1;
        ${theme.mediaQueries.desktop} {
            top: 51px;
        }
    `}
`;

const MatrixPopoverContainer = styled(PopoverContainer)`
    ${({ theme }) => css`
        left: -138px;
        ${theme.mediaQueries.desktop} {
            left: -134px;
        }
    `}
`;

const ColorPopoverContainer = styled(PopoverContainer)`
    ${({ theme }) => css`
        left: -96px;

        ${theme.mediaQueries.desktop} {
            left: -92px;
        }
    `}
`;

const Toolbar = ({ editor, onChange }) => {
    const [showMatrixModal, setShowMatrixModal] = useState(false);
    const [showColorPopover, setShowColorPopover] = useState(false);

    const matrixPopoverRef = useRef(null);
    const colorPopoverRef = useRef(null);

    useMouseDownOutside(matrixPopoverRef, () => {
        setShowMatrixModal(false);
    });

    useMouseDownOutside(colorPopoverRef, () => {
        setShowColorPopover(false);
    });

    const toolbarButtons = [
        { type: 'fraction', action: () => editor.action('fraction') },
        { type: 'squareRoot', action: () => editor.action('squareRoot') },
        { type: 'nRoot', action: () => editor.action('nRoot') },
        { type: 'superScript', action: () => editor.action('superscript') },
        { type: 'encloseTop', action: () => editor.action('encloseTop') },
        {
            type: 'matrix',
            action: () => {
                setShowMatrixModal(true);
                editor
                    .getEditorModel()
                    .insertMathML(
                        '<math xmlns="http://www.w3.org/1998/Math/MathML"><mfenced open="|" close="|"><mtable><mtr><mtd/><mtd/></mtr><mtr><mtd/><mtd/></mtr></mtable></mfenced></math>',
                    );
            },
            popoverActions: {
                deleteMatrix: () => editor.action('undo'),
                printMatrix: (matrix) => editor.getEditorModel().insertMathML(matrix),
            },
        },
        { type: 'lCurlyColumn', action: () => editor.action('lCurlyColumn') },
        { type: 'greatherThan', action: () => editor.getEditorModel().insertMathML('&gt;', 0) },
        { type: 'greatherEqual', action: () => editor.getEditorModel().insertMathML('&#8805;', 0) },
        { type: 'lessThan', action: () => editor.getEditorModel().insertMathML('&lt;', 0) },
        { type: 'lessEqual', action: () => editor.getEditorModel().insertMathML('&#8804;', 0) },
        { type: 'notEqual', action: () => editor.getEditorModel().insertMathML('≠', 0) },
        { type: 'plusMinus', action: () => editor.getEditorModel().insertMathML('&#177;', 0) },
        {
            type: 'color',
            action: () => setShowColorPopover(true),
            popoverActions: {
                setColor: (color) => editor.actionWithParam('setColor', color),
            },
        },
        { type: 'parenthesis', action: () => editor.action('parenthesis') },
        { type: 'squareBracket', action: () => editor.action('squareBracket') },
        { type: 'absolute', action: () => editor.action('verticalBar') },
        { type: 'curlyBracket', action: () => editor.action('curlyBracket') },
    ];

    return (
        <Container>
            {toolbarButtons.map((button) => {
                return (
                    <ButtonContainer key={button.type}>
                        <Button
                            onClick={() => {
                                editor.focus();
                                button.action();
                                const content = editor.getMathML().replace('<mspace linebreak="newline"/>', '');
                                onChange(content);
                            }}
                        >
                            <Icon name={button.type} />
                        </Button>
                        {showMatrixModal && button.type === 'matrix' && (
                            <MatrixPopoverContainer ref={matrixPopoverRef}>
                                <MatrixPopover
                                    deleteMatrix={() => {
                                        button.popoverActions.deleteMatrix();
                                    }}
                                    printMatrix={(matrix) => {
                                        button.popoverActions.printMatrix(matrix);
                                    }}
                                />
                            </MatrixPopoverContainer>
                        )}
                        {showColorPopover && button.type === 'color' && (
                            <ColorPopoverContainer ref={colorPopoverRef}>
                                <ColorPopover
                                    setColor={(color) => {
                                        button.popoverActions.setColor(color);
                                        setShowColorPopover(false);
                                    }}
                                />
                            </ColorPopoverContainer>
                        )}
                    </ButtonContainer>
                );
            })}
        </Container>
    );
};

Toolbar.propTypes = {
    editor: PropTypes.object,
    onChange: PropTypes.func,
};

export default Toolbar;
